import React, {
    useState,
    useRef,
    MouseEvent,
    useEffect,
    useCallback,
} from 'react';
import { format as formatDate } from 'date-fns';
import { es } from 'date-fns/locale';
import { flip, offset, shift, useFloating } from '@floating-ui/react-dom';

import DateIcon from '../../icons/date-icon/date-icon';

import styles from './date-picker.module.scss';
import { DatePickerModal } from './modal';

interface IDate {
    day: number;
    date: number;
    month: number;
    year: number;
}

interface Props {
    onDate?: (date: Date) => void;
    initialDate?: Date;
}

const DatePicker = (props: Props) => {
    const { refs, floatingStyles, elements } = useFloating({
        middleware: [offset(8), flip(), shift({ padding: 8 })],
    });

    const [date, setDate] = useState(props.initialDate ?? new Date());
    const skipFirstEmit = useRef(props.initialDate !== undefined);
    const [modalShown, setModalShown] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(
        props.initialDate ?? new Date()
    );
    const [daysOfMonth, setDaysOfMonth] = useState<IDate[]>([]);
    const [view, setView] = useState<'days' | 'years'>('days');
    const [yearGridStart, setYearGridStart] = useState(
        () => currentMonth.getFullYear() - 6
    );

    useEffect(() => {
        if (!modalShown) return;

        const handleOutsideClick = (ev: MouseEvent) => {
            const target = (ev.target as HTMLElement).closest(
                '[role="datepicker-modal"]'
            );
            if (!target) setModalShown(false);
        };

        document.addEventListener('mousedown', handleOutsideClick as any);
        return () => {
            document.removeEventListener(
                'mousedown',
                handleOutsideClick as any
            );
        };
    }, [modalShown, elements.reference, elements.floating]);

    useEffect(() => {
        getDaysOfCurrentMonth();
    }, [currentMonth]);

    useEffect(() => {
        if (skipFirstEmit.current) {
            skipFirstEmit.current = false;
            return;
        }
        props.onDate?.(date);
    }, [date]);

    const getDaysOfCurrentMonth = () => {
        const date = new Date();
        date.setDate(1); // Set it to the first day of the month
        date.setMonth(currentMonth.getMonth());
        date.setFullYear(currentMonth.getFullYear());
        date.setHours(0, 0, 0, 0); // Set date to midnight: timestamp 00:00:00

        const listOfDays = [];
        while (date.getMonth() === currentMonth.getMonth()) {
            listOfDays.push({
                day: date.getDay(),
                date: date.getDate(),
                month: date.getMonth(),
                year: date.getFullYear(),
            });

            date.setDate(date.getDate() + 1);
        }
        setDaysOfMonth(listOfDays);
    };

    const updateCurrentMonth = (leap: number) => {
        const newMonth = currentMonth.setMonth(currentMonth.getMonth() + leap);
        setCurrentMonth(new Date(newMonth));
    };

    const goPrev = () => {
        if (view === 'days') updateCurrentMonth(-1);
        else setYearGridStart((s) => s - 12);
    };

    const goNext = () => {
        if (view === 'days') updateCurrentMonth(1);
        else setYearGridStart((s) => s + 12);
    };

    const onYearSelect = (year: number) => {
        const next = new Date(currentMonth);
        next.setFullYear(year);
        setCurrentMonth(next);
        setView('days');
    };

    const yearRange = Array.from({ length: 12 }, (_, i) => yearGridStart + i);

    const onDateSelect = (date: IDate) => {
        const selectedDate = new Date(date.year, date.month, date.date);
        setDate(selectedDate);
        setModalShown(false);
    };

    const toggleShowModal = useCallback(
        (ev: MouseEvent) => {
            ev.preventDefault();
            setModalShown(!modalShown);
        },
        [modalShown]
    );

    return (
        <div className={styles.container}>
            <button
                ref={refs.setReference}
                onClick={toggleShowModal}
                className={styles.mainButton}
            >
                <DateIcon size={16} color="#414552" />
                <span className={styles.formattedDate}>
                    {formatDate(date, 'dd MMMM, yyyy', { locale: es })}
                </span>
            </button>
            {modalShown && (
                <DatePickerModal
                    ref={refs.setFloating}
                    styles={floatingStyles}
                    view={view}
                    days={daysOfMonth}
                    yearRange={yearRange}
                    selectedDate={date}
                    onDateSelect={onDateSelect}
                    onYearSelect={onYearSelect}
                    onToggleView={() =>
                        setView((v) => {
                            if (v === 'days') {
                                setYearGridStart(
                                    currentMonth.getFullYear() - 6
                                );
                            }
                            return v === 'days' ? 'years' : 'days';
                        })
                    }
                    prevMonth={goPrev}
                    nextMonth={goNext}
                    onClose={() => setModalShown(false)}
                />
            )}
        </div>
    );
};

export default DatePicker;
